/**
 * Störungen — Betrieb (Phase B).
 *
 * Der laufende Tag: Welche Störungen sind JETZT offen, wer hat sie gemeldet,
 * seit wann — und das Beenden mit einem Griff (In-App-Bestätigung, kein
 * Browser-Dialog). Dazu der auditierte Nachtrag (Meldung #19): eine Störung,
 * die niemand live gemeldet hat, mit Tag + Von–Bis nachtragen; die Schicht
 * rechnet der Server aus dem Von-Zeitpunkt.
 *
 * Historie und Auswertung wohnen bewusst NICHT hier, sondern unter
 * Auswerten → Störungen — diese Seite ist die Werkbank, nicht das Archiv.
 * Langläufer über 12 Stunden stehen getrennt: Erfahrungswert aus dem Ist,
 * das sind fast immer vergessene Meldungen, keine echten Ausfälle.
 */

import { useCallback, useEffect, useState } from 'react';
import { getTodayBerlin } from '@wash-ops/shared';
import { useAuth } from '../App';
import { LeitstandRahmen } from '../components/leitstand/LeitstandRahmen';
import { Karte, Kennung, Kennzahl, Notiz, Zustand } from '../components/leitstand';

interface LaufendeStoerung {
  id: string;
  startedAt: string;
  location: string | null;
  visibility: 'ALL' | 'ADMIN_ONLY';
  categoryNameSnapshot: string | null;
  description: string | null;
  machine: { id: string; name: string } | null;
  station: { id: string; name: string } | null;
  recordedBy: { id: string; displayName: string } | null;
}

interface Kategorie {
  id: string;
  name: string;
}

interface Maschine {
  id: string;
  name: string;
}

const LANGLAEUFER_STUNDEN = 12;

function dauerText(startedAt: string): string {
  const min = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 60000));
  if (min < 60) return `seit ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `seit ${h} h ${min % 60} min`;
  return `seit ${Math.floor(h / 24)} Tagen`;
}

function beginnText(startedAt: string): string {
  return new Date(startedAt).toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function istLanglaeufer(s: LaufendeStoerung): boolean {
  return Date.now() - new Date(s.startedAt).getTime() > LANGLAEUFER_STUNDEN * 3600000;
}

export default function DisruptionsBetrieb() {
  const { user } = useAuth();

  const [stoerungen, setStoerungen] = useState<LaufendeStoerung[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  // Beenden: zweistufig — erst der Knopf an der Karte, dann die Bestätigung dort
  const [beendenId, setBeendenId] = useState<string | null>(null);
  const [beende, setBeende] = useState(false);
  const [aktionsFehler, setAktionsFehler] = useState('');

  // Nachtrag-Formular
  const [nachtragOffen, setNachtragOffen] = useState(false);
  const [maschinen, setMaschinen] = useState<Maschine[]>([]);
  const [kategorien, setKategorien] = useState<Kategorie[]>([]);
  const [ntTag, setNtTag] = useState(getTodayBerlin(new Date()));
  const [ntVon, setNtVon] = useState('');
  const [ntBis, setNtBis] = useState('');
  const [ntMaschine, setNtMaschine] = useState('');
  const [ntOrt, setNtOrt] = useState('');
  const [ntKategorie, setNtKategorie] = useState('');
  const [ntText, setNtText] = useState('');
  const [ntSende, setNtSende] = useState(false);
  const [ntFehler, setNtFehler] = useState('');
  const [ntErfolg, setNtErfolg] = useState('');

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch('/api/disruptions/active', { credentials: 'include' });
      if (!res.ok) throw new Error('Die laufenden Störungen konnten nicht geladen werden');
      const body = await res.json();
      setStoerungen(Array.isArray(body) ? body : []);
    } catch (e) {
      setFehler(
        e instanceof Error ? e.message : 'Die laufenden Störungen konnten nicht geladen werden',
      );
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    void lade();
  }, [lade]);

  // Stammdaten fürs Nachtrag-Formular erst beim Aufklappen laden
  useEffect(() => {
    if (!nachtragOffen || maschinen.length > 0) return;
    void (async () => {
      try {
        const [mRes, kRes] = await Promise.all([
          fetch('/api/data/machines', { credentials: 'include' }),
          fetch('/api/disruptions/categories', { credentials: 'include' }),
        ]);
        if (mRes.ok) {
          const m = await mRes.json();
          setMaschinen(Array.isArray(m) ? m : (m.machines ?? []));
        }
        if (kRes.ok) {
          const k = await kRes.json();
          setKategorien(Array.isArray(k) ? k : (k.categories ?? []));
        }
      } catch {
        // Formular bleibt nutzbar — Ort-Freitext geht auch ohne Maschinenliste
      }
    })();
  }, [nachtragOffen, maschinen.length]);

  const beendeStoerung = async (id: string) => {
    setBeende(true);
    setAktionsFehler('');
    try {
      const res = await fetch(`/api/disruptions/${id}/end`, {
        method: 'PATCH',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Die Störung konnte nicht beendet werden');
      }
      setBeendenId(null);
      await lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Die Störung konnte nicht beendet werden');
    } finally {
      setBeende(false);
    }
  };

  const sendeNachtrag = async () => {
    if (!ntVon || !ntBis) {
      setNtFehler('Bitte Von- und Bis-Uhrzeit angeben.');
      return;
    }
    if (!ntMaschine && !ntOrt.trim()) {
      setNtFehler('Bitte Maschine wählen oder den Ort eintragen.');
      return;
    }
    setNtSende(true);
    setNtFehler('');
    try {
      const res = await fetch('/api/disruptions/nachtrag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tag: ntTag,
          vonZeit: ntVon,
          bisZeit: ntBis,
          ...(ntMaschine ? { machineId: ntMaschine } : { location: ntOrt.trim() }),
          ...(ntKategorie ? { categoryId: ntKategorie } : {}),
          ...(ntText.trim() ? { description: ntText.trim() } : {}),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Der Nachtrag konnte nicht gespeichert werden');
      }
      setNtErfolg(
        `Nachtrag gespeichert (${ntTag.split('-').reverse().join('.')}, ${ntVon}–${ntBis} Uhr) — er steht ab jetzt in der Auswertung.`,
      );
      setNachtragOffen(false);
      setNtVon('');
      setNtBis('');
      setNtMaschine('');
      setNtOrt('');
      setNtKategorie('');
      setNtText('');
    } catch (e) {
      setNtFehler(e instanceof Error ? e.message : 'Der Nachtrag konnte nicht gespeichert werden');
    } finally {
      setNtSende(false);
    }
  };

  const frische = stoerungen.filter((s) => !istLanglaeufer(s));
  const langlaeufer = stoerungen.filter(istLanglaeufer);

  const karteFuer = (s: LaufendeStoerung) => {
    const wo = s.machine?.name ?? s.location ?? s.station?.name ?? 'Ohne Ort';
    const bestaetigt = beendenId === s.id;
    return (
      <Karte
        key={s.id}
        titel={wo}
        marke={`${dauerText(s.startedAt)} · seit ${beginnText(s.startedAt)} Uhr`}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Zustand art="stoerung">läuft</Zustand>
            {s.categoryNameSnapshot && <Kennung>{s.categoryNameSnapshot}</Kennung>}
            {s.visibility === 'ADMIN_ONLY' && <Kennung>Test — für Stationen unsichtbar</Kennung>}
          </div>

          {s.description && <p className="text-sm text-tinte-2">{s.description}</p>}

          <p className="text-[13px] text-tinte-3">
            Gemeldet von {s.recordedBy?.displayName ?? 'unbekannt'}
            {s.station && ` · Station ${s.station.name}`}
          </p>

          {aktionsFehler && bestaetigt && <p className="text-[13px] text-stoer">{aktionsFehler}</p>}

          <div className="flex flex-wrap gap-2">
            {!bestaetigt ? (
              <button
                onClick={() => {
                  setBeendenId(s.id);
                  setAktionsFehler('');
                }}
                className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz"
              >
                Störung beenden
              </button>
            ) : (
              <>
                <span className="self-center text-[13px] text-tinte-2">
                  Jetzt als behoben eintragen?
                </span>
                <button
                  onClick={() => void beendeStoerung(s.id)}
                  disabled={beende}
                  className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz disabled:opacity-50"
                >
                  Ja, beenden
                </button>
                <button
                  onClick={() => setBeendenId(null)}
                  disabled={beende}
                  className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2"
                >
                  Abbrechen
                </button>
              </>
            )}
          </div>
        </div>
      </Karte>
    );
  };

  const eingabeKlasse =
    'rounded-[4px] border border-linie-2 bg-flaeche px-3 py-2 text-sm text-tinte';
  const markeKlasse = 'font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3';

  return (
    <LeitstandRahmen titel="Störungen" benutzer={user?.displayName}>
      {fehler && (
        <Notiz titel="Konnte nicht laden">
          {fehler}{' '}
          <button onClick={() => void lade()} className="underline">
            Erneut versuchen
          </button>
        </Notiz>
      )}

      {laden && !stoerungen.length && !fehler && (
        <p className="py-12 text-center text-sm text-tinte-3">Lade laufende Störungen …</p>
      )}

      {!laden && !fehler && (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kennzahl
              marke="Laufende Störungen"
              wert={String(frische.length)}
              tendenz={frische.length > 0 ? 'runter' : 'neutral'}
              neben={frische.length === 0 ? 'alles läuft' : undefined}
            />
            <Kennzahl
              marke={`Länger als ${LANGLAEUFER_STUNDEN} h`}
              klein
              wert={String(langlaeufer.length)}
              tendenz={langlaeufer.length > 0 ? 'runter' : 'neutral'}
              neben={langlaeufer.length > 0 ? 'vermutlich vergessen' : undefined}
            />
            <Kennzahl
              marke="Stand"
              klein
              wert={getTodayBerlin(new Date()).split('-').reverse().join('.')}
            />
          </div>

          {ntErfolg && (
            <Notiz titel="Nachtrag gespeichert">
              {ntErfolg}{' '}
              <button onClick={() => setNtErfolg('')} className="underline">
                Ausblenden
              </button>
            </Notiz>
          )}

          {stoerungen.length === 0 && (
            <Karte titel="Laufende Störungen" marke="live von den Stationen">
              <p className="py-6 text-center text-sm text-tinte-3">
                Keine laufende Störung — nichts ist offen gemeldet.
              </p>
            </Karte>
          )}

          {frische.map(karteFuer)}

          {langlaeufer.length > 0 && (
            <>
              <Notiz titel={`Länger als ${LANGLAEUFER_STUNDEN} Stunden offen`}>
                So lange steht in der Wäscherei praktisch nie eine Maschine — meist wurde das
                Beenden vergessen. Bitte prüfen und beenden; die Dauer fließt sonst falsch in die
                Auswertung ein.
              </Notiz>
              {langlaeufer.map(karteFuer)}
            </>
          )}

          <Karte titel="Störung nachtragen" marke="wenn live keine Meldung erfasst wurde">
            {!nachtragOffen ? (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-tinte-2">
                  Trägt eine bereits vorbeigegangene Störung mit Tag und Von–Bis-Zeit nach. Die
                  Schicht ordnet das System aus dem Von-Zeitpunkt zu; der Nachtrag wird im Protokoll
                  als solcher gekennzeichnet.
                </p>
                <div>
                  <button
                    onClick={() => {
                      setNachtragOffen(true);
                      setNtErfolg('');
                    }}
                    className="rounded-[4px] border border-linie-stark bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2"
                  >
                    Nachtrag erfassen
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="grid gap-3 sm:grid-cols-3">
                  <label className="flex flex-col gap-1">
                    <span className={markeKlasse}>Tag</span>
                    <input
                      type="date"
                      value={ntTag}
                      max={getTodayBerlin(new Date())}
                      onChange={(e) => setNtTag(e.target.value)}
                      className={eingabeKlasse}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className={markeKlasse}>Von</span>
                    <input
                      type="time"
                      value={ntVon}
                      onChange={(e) => setNtVon(e.target.value)}
                      className={eingabeKlasse}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className={markeKlasse}>Bis</span>
                    <input
                      type="time"
                      value={ntBis}
                      onChange={(e) => setNtBis(e.target.value)}
                      className={eingabeKlasse}
                    />
                  </label>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1">
                    <span className={markeKlasse}>Maschine</span>
                    <select
                      value={ntMaschine}
                      onChange={(e) => setNtMaschine(e.target.value)}
                      className={eingabeKlasse}
                    >
                      <option value="">— keine Maschine —</option>
                      {maschinen.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className={markeKlasse}>Ort (wenn keine Maschine)</span>
                    <input
                      type="text"
                      value={ntOrt}
                      onChange={(e) => setNtOrt(e.target.value)}
                      placeholder="z. B. Heizung, Druckluft …"
                      disabled={!!ntMaschine}
                      className={`${eingabeKlasse} disabled:opacity-50`}
                    />
                  </label>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1">
                    <span className={markeKlasse}>Kategorie</span>
                    <select
                      value={ntKategorie}
                      onChange={(e) => setNtKategorie(e.target.value)}
                      className={eingabeKlasse}
                    >
                      <option value="">— ohne Kategorie —</option>
                      {kategorien.map((k) => (
                        <option key={k.id} value={k.id}>
                          {k.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className={markeKlasse}>Was war los?</span>
                    <input
                      type="text"
                      value={ntText}
                      onChange={(e) => setNtText(e.target.value)}
                      placeholder="kurze Beschreibung (optional)"
                      className={eingabeKlasse}
                    />
                  </label>
                </div>

                {ntFehler && <p className="text-[13px] text-stoer">{ntFehler}</p>}

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => void sendeNachtrag()}
                    disabled={ntSende}
                    className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz disabled:opacity-50"
                  >
                    Nachtrag speichern
                  </button>
                  <button
                    onClick={() => {
                      setNachtragOffen(false);
                      setNtFehler('');
                    }}
                    disabled={ntSende}
                    className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            )}
          </Karte>

          <p className="text-[13px] text-tinte-3">
            Beendete Störungen, Dauern und Ranglisten stehen unter{' '}
            <a href="/reports/disruptions" className="underline">
              Auswerten → Störungen
            </a>
            .
          </p>
        </div>
      )}
    </LeitstandRahmen>
  );
}
